import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { Summary } from "@lib/titan2-slingo/dist/game/view/abstract/display/Summary";
import { SlingoLang } from "@lib/titan2-slingo/dist/game/view/abstract/lang/SlingoLang";
import { DataObject } from "@lib/titan2/dist/api/core/data/DataObject";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { CollectWinResultData } from "../../../model/data/CollectWinResultData";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameLang } from "../lang/GameLang";
/**
 * GameSummary.ts
 */
export class GameSummary extends Summary {
    create() {
        this.createModel();
        this.createWinAmountField();
        this.createTitleField();
        this.panel = TitanSpine.createSpine(this, this.landscapeProperties.panelSpineProperties, this.portraitProperties?.panelSpineProperties);
        this.totalWinTitleField = TitanText.createField(null, this.landscapeProperties.totalWinTitleFieldProperties, this.portraitProperties?.totalWinTitleFieldProperties);
        this.slingoWinTitleField = TitanText.createField(null, this.landscapeProperties.slingoCellProperties.nameFieldProperties, this.portraitProperties?.slingoCellProperties.nameFieldProperties);
        this.slingoWinValueField = TitanText.createField(null, this.landscapeProperties.slingoCellProperties.amountFieldProperties, this.portraitProperties?.slingoCellProperties.amountFieldProperties);
        this.jokerWinTitleField = TitanText.createField(null, this.landscapeProperties.jokerCellProperties.nameFieldProperties, this.portraitProperties?.jokerCellProperties.nameFieldProperties);
        this.jokerWinValueField = TitanText.createField(null, this.landscapeProperties.jokerCellProperties.amountFieldProperties, this.portraitProperties?.jokerCellProperties.amountFieldProperties);
        this.coinWinTitleField = TitanText.createField(null, this.landscapeProperties.symbolWinCellProperties.nameFieldProperties, this.portraitProperties?.symbolWinCellProperties.nameFieldProperties);
        this.coinWinValueField = TitanText.createField(null, this.landscapeProperties.symbolWinCellProperties.amountFieldProperties, this.portraitProperties?.symbolWinCellProperties.amountFieldProperties);
        this.panel.onComplete(this.summaryComplete, this);
    }
    createBackground() {
        this.background = TitanSprite.createSprite(this, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties.backgroundProperties);
    }
    update() {
        this.result = this.model.getResultData();
    }
    updateTitle() {
        let key;
        if (this.result.isFullHouse() && !this.result.isLowWin()) {
            key = SlingoLang.FULL_HOUSE;
        }
        else if (this.result.isBigWin() && !this.result.isLowWin()) {
            key = SlingoLang.BIG_WIN;
        }
        else if (this.result.isLowWin() || this.result.isSmallWin()) {
            key = GameLang.RESULT; // use RESULT instead of RESULTS as per https://gamingrealms.atlassian.net/browse/STUDIOWP-96
        }
        else {
            key = SlingoLang.BIG_WIN_1;
        }
        this.titleField.setText(this.langManager.getLang(key), true);
        this.titleField.visible = true;
    }
    updateCells() {
        [
            this.totalWinTitleField,
            this.slingoWinTitleField,
            this.slingoWinValueField,
            this.jokerWinTitleField,
            this.jokerWinValueField,
            this.coinWinTitleField,
            this.coinWinValueField
        ].forEach(field => field.parent?.removeChild(field));
        this.panel.visible = false;
        const matchedPatterns = this.model.getMatchedPatterns().size();
        const slingoLabel = this.langManager.getLang(SlingoLang.SLINGO_CELL).replace("{param.matchedPatterns}", String(matchedPatterns));
        this.slingoWinTitleField.setText(slingoLabel, true);
        const slingoWin = this.result.getSlingoWin();
        const otherWinnings = this.model.getCollectWinnings() ?? new CollectWinResultData(new DataObject());
        const scatterWins = otherWinnings.getScatterWin();
        const winlineWins = otherWinnings.getWinLineWin();
        const wins = [
            { win: this.result.hasSlingoWin(), amount: slingoWin, titleField: this.slingoWinTitleField, valueField: this.slingoWinValueField },
            { win: scatterWins > 0, amount: scatterWins, titleField: this.jokerWinTitleField, valueField: this.jokerWinValueField },
            { win: winlineWins > 0, amount: winlineWins, titleField: this.coinWinTitleField, valueField: this.coinWinValueField }
        ].filter(winData => winData.win);
        this.updateTitle();
        if (wins.length > 0) {
            this.slingoWinTitleField.setText(this.langManager.getLang(SlingoLang.SLINGO_CELL, [new KeyValuePair("{param.matchedPatterns}", this.model.getMatchedPatterns().size())]), true);
            wins.forEach(({ amount, titleField, valueField }, winIndex) => {
                valueField.setText(this.formatCurrency(amount), true);
                GameSpineUtil.addToSlot(this.panel, titleField, `dynamic-slot-label-${winIndex + 1}`);
                GameSpineUtil.addToSlot(this.panel, valueField, `dynamic-slot-value-${winIndex + 1}`);
            });
            this.winAmountField.setText(this.formatCurrency(this.model.getTotalWin()), true);
            GameSpineUtil.addToSlot(this.panel, this.totalWinTitleField, "dynamic-slot-label-4");
            GameSpineUtil.addToSlot(this.panel, this.winAmountField, "dynamic-slot-value-4");
            this.updateTitle();
        }
        else {
            this.titleField.setText(this.langManager.getLang(SlingoLang.GAME_OVER), true);
        }
        GameSpineUtil.addToSlot(this.panel, this.titleField, "dynamic-slot-label-0");
        return wins.length;
    }
    play() {
        const wins = this.updateCells();
        this.panel.play(`panel-${wins}`, false, false, 0);
        this.panel.getSpine().update(0.01);
        this.panel.visible = true;
    }
    summaryComplete() {
        this.handleDialogComplete();
        this.dispatchGame(SlingoEvent.GAME_SUMMARY_COMPLETE);
    }
    reset() {
    }
    handleDialogComplete() {
        //comment these out to leave the complete summary on the screen for layout
        this.reset();
        this.callHandler(this.completeHandler);
    }
}
//# sourceMappingURL=GameSummary.js.map